import React, { useCallback, useEffect, useState } from 'react';
import './Admin.css';

// Private team dashboard. Signs in with Supabase Auth (email + password) and reads everything from
// the admin_dashboard RPC, which only answers for accounts in public.admin_users.
const SESSION_KEY = 'wio-admin-session';

const TOPICS = {
  account: 'Account & sign-in',
  steps: 'Steps & syncing',
  challenges: 'Challenges & friends',
  bug: 'Something isn’t working',
  feedback: 'Feedback or idea',
  other: 'Something else',
};

function configuration() {
  return {
    url: process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, ''),
    key: process.env.REACT_APP_SUPABASE_ANON_KEY,
  };
}

async function request(path, body, token) {
  const { url, key } = configuration();
  if (!url || !key) throw new Error('Supabase is not configured for this site.');
  const response = await fetch(`${url}${path}`, {
    method: 'POST',
    headers: { apikey: key, Authorization: `Bearer ${token || key}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(json?.msg || json?.error_description || json?.message || 'Request failed');
    error.status = response.status;
    error.code = json?.code;
    throw error;
  }
  return json;
}

function toSession(json) {
  return { access: json.access_token, refresh: json.refresh_token, expiresAt: Date.now() + json.expires_in * 1000 };
}

function loadSession() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

function saveSession(session) {
  if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else sessionStorage.removeItem(SESSION_KEY);
}

async function freshSession(session) {
  if (session.expiresAt - Date.now() > 60_000) return session;
  const next = toSession(await request('/auth/v1/token?grant_type=refresh_token', { refresh_token: session.refresh }));
  saveSession(next);
  return next;
}

const number = (value) => Number(value || 0).toLocaleString('en-US');
const shortDate = (value) =>
  new Date(`${value}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dateTime = (value) =>
  new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function Stat({ label, value, hint, onClick }) {
  const content = (
    <>
      <span className="admin-stat-label">{label}</span>
      <strong className="admin-stat-value">{value}</strong>
      {hint && <span className="admin-stat-hint">{hint}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className="admin-stat admin-stat-link" onClick={onClick}>{content}</button>
  ) : (
    <div className="admin-stat">{content}</div>
  );
}

// Single-series daily bar chart: one hue, no legend (the title names the series). Hovering a bar
// highlights it and shows its value in the header, where it can't cover the bars.
function DailyBars({ title, data, unit }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...data.map((d) => d.count));
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const width = 600;
  const height = 160;
  const slot = width / data.length;
  const bar = Math.max(2, slot - 2);
  const active = hover == null ? null : data[hover];

  return (
    <figure className="admin-card admin-chart">
      <figcaption>
        <h3>{title}</h3>
        <span className="admin-muted admin-chart-readout" aria-live="polite">
          {active ? (
            <><strong>{number(active.count)}</strong> {unit} · {shortDate(active.date)}</>
          ) : (
            <>{number(total)} {unit} in 30 days</>
          )}
        </span>
      </figcaption>
      <div className="admin-chart-plot" onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={`${title}, last 30 days`}>
          <line x1="0" x2={width} y1={height - 0.5} y2={height - 0.5} className="admin-axis" />
          {data.map((d, i) => {
            const h = d.count === 0 ? 0 : Math.max(3, (d.count / max) * (height - 8));
            const x = i * slot + (slot - bar) / 2;
            const r = Math.min(4, bar / 2, h);
            return (
              <g key={d.date} onMouseEnter={() => setHover(i)}>
                <rect x={i * slot} y="0" width={slot} height={height} fill="transparent" />
                {h > 0 && (
                  <path
                    className={hover === i ? 'admin-bar admin-bar-active' : 'admin-bar'}
                    d={`M${x},${height} V${height - h + r} Q${x},${height - h} ${x + r},${height - h} H${x + bar - r} Q${x + bar},${height - h} ${x + bar},${height - h + r} V${height} Z`}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="admin-chart-axis admin-muted">
        <span>{shortDate(data[0].date)}</span>
        <span>{shortDate(data[data.length - 1].date)}</span>
      </div>
    </figure>
  );
}

function SignIn({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const session = toSession(await request('/auth/v1/token?grant_type=password', { email, password }));
      saveSession(session);
      onSignedIn(session);
    } catch (err) {
      setError(err.status === 400 ? 'Email or password is incorrect.' : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="admin-signin">
      <form className="admin-card" onSubmit={submit}>
        <img src="/walkitofflogo-dark.svg" alt="Walk It Off" width="175" height="65" />
        <h1>Team dashboard</h1>
        <label>
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        </label>
        <label>
          <span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        {error && <p className="admin-error" role="alert">{error}</p>}
        <button type="submit" className="admin-button" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </main>
  );
}

const TABS = [
  ['overview', 'Overview'],
  ['users', 'Users'],
  ['challenges', 'Challenges'],
];

function Dashboard({ data, onRefresh, onSignOut, onConfirmEmail, refreshing }) {
  const [tab, setTab] = useState('overview');
  const [usersFilter, setUsersFilter] = useState('all');
  const showUnconfirmed = () => {
    setUsersFilter('unconfirmed');
    setTab('users');
  };
  return (
    <div className="admin">
      <header className="admin-header">
        <img src="/walkitofflogo-dark.svg" alt="Walk It Off" width="140" height="52" />
        <nav className="admin-tabs" aria-label="Dashboard sections">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? 'admin-tab admin-tab-active' : 'admin-tab'}
              aria-current={tab === key ? 'page' : undefined}
              onClick={() => {
                setUsersFilter('all');
                setTab(key);
              }}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="admin-header-actions">
          <span className="admin-muted">Updated {dateTime(data.generated_at)}</span>
          <button type="button" className="admin-button admin-button-ghost" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="admin-button admin-button-ghost" onClick={onSignOut}>Sign out</button>
        </div>
      </header>

      {tab === 'overview' && <Overview data={data} onShowUnconfirmed={showUnconfirmed} />}
      {tab === 'users' && <UsersList users={data.users_list} initialFilter={usersFilter} onConfirmEmail={onConfirmEmail} />}
      {tab === 'challenges' && <ChallengesList challenges={data.challenges_list} />}
    </div>
  );
}

function Overview({ data, onShowUnconfirmed }) {
  const { users, engagement, challenges, social, support } = data;
  return (
      <main className="admin-main">
        <section>
          <h2>Users</h2>
          <div className="admin-stats">
            <Stat label="Total users" value={number(users.total)} />
            <Stat label="New today" value={number(users.new_today)} hint="UTC" />
            <Stat label="New, last 7 days" value={number(users.new_7d)} />
            <Stat label="New, last 30 days" value={number(users.new_30d)} />
            <Stat
              label="Unconfirmed email"
              value={number(users.unconfirmed_email)}
              hint="Never clicked the link · View and confirm →"
              onClick={users.unconfirmed_email > 0 ? onShowUnconfirmed : undefined}
            />
            <Stat label="Set a step goal" value={number(users.with_goal)} />
          </div>
          <div className="admin-grid">
            <DailyBars title="Sign-ups per day" data={users.signups_by_day} unit="sign-ups" />
            <div className="admin-card">
              <h3>Sign-up method</h3>
              <ul className="admin-breakdown">
                {users.by_provider.map((p) => (
                  <li key={p.provider}>
                    <span className="admin-breakdown-label">{p.provider === 'email' ? 'Email' : p.provider[0].toUpperCase() + p.provider.slice(1)}</span>
                    <span className="admin-breakdown-track"><span style={{ width: `${(p.count / Math.max(1, users.total)) * 100}%` }} /></span>
                    <span className="admin-breakdown-value">{number(p.count)}</span>
                  </li>
                ))}
              </ul>
              <p className="admin-muted admin-footnote">{number(users.referred)} joined through a friend’s referral link.</p>
            </div>
          </div>
        </section>

        <section>
          <h2>Activity</h2>
          {data.active_now && <ActiveNow active={data.active_now} />}
          <div className="admin-stats">
            <Stat label="Synced steps today" value={number(engagement.active_today)} hint="UTC" />
            <Stat label="Active, last 7 days" value={number(engagement.active_7d)} />
            <Stat label="Active, last 30 days" value={number(engagement.active_30d)} />
            <Stat label="Avg steps per day" value={number(engagement.avg_steps_7d)} hint="Last 7 days" />
            <Stat label="Goal hit rate" value={engagement.goal_hit_rate_7d == null ? '–' : `${engagement.goal_hit_rate_7d}%`} hint="Last 7 days, days with data" />
            <Stat label="iPhone / Android" value={`${number(engagement.ios_users_30d)} / ${number(engagement.android_users_30d)}`} hint="Active, last 30 days" />
          </div>
          <DailyBars title="Users who synced steps" data={engagement.active_by_day} unit="user-days" />
        </section>

        <section>
          <h2>Challenges</h2>
          <div className="admin-stats">
            <Stat label="Total challenges" value={number(challenges.total)} />
            <Stat label="Active now" value={number(challenges.active)} />
            <Stat label="Upcoming" value={number(challenges.upcoming)} />
            <Stat label="Ended" value={number(challenges.ended)} />
            <Stat label="Public / private" value={`${number(challenges.public)} / ${number(challenges.private)}`} />
            <Stat label="Memberships" value={number(challenges.memberships)} hint="People × challenges" />
          </div>
          <div className="admin-card admin-table-wrap">
            <h3>Biggest challenges</h3>
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Status</th><th>Starts</th><th>Days</th><th>Visibility</th><th className="admin-num">Members</th></tr>
              </thead>
              <tbody>
                {challenges.top.map((c) => (
                  <tr key={`${c.name}-${c.start_date}`}>
                    <td>{c.name}{c.official && <span className="admin-tag">Official</span>}</td>
                    <td><span className={`admin-status admin-status-${c.status}`}>{c.status}</span></td>
                    <td>{shortDate(c.start_date)}</td>
                    <td>{c.days}</td>
                    <td>{c.visibility}</td>
                    <td className="admin-num">{number(c.members)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2>Social & support</h2>
          <div className="admin-stats">
            <Stat label="Friendships" value={number(social.friendships)} />
            <Stat label="Pending friend requests" value={number(social.pending_requests)} />
            <Stat label="Supporter links" value={number(social.supporter_links)} />
            <Stat label="Support messages" value={number(support.in_app_7d + support.web_7d)} hint={`Last 7 days · ${number(support.in_app_7d)} app, ${number(support.web_7d)} web`} />
            <Stat label="Open challenge reports" value={number(support.open_reports)} />
            <Stat label="Open disputes" value={number(support.open_disputes)} />
          </div>
          <div className="admin-card">
            <h3>Latest support messages</h3>
            {support.recent.length === 0 ? (
              <p className="admin-muted">No messages yet.</p>
            ) : (
              <ul className="admin-messages">
                {support.recent.map((m, i) => (
                  <li key={`${m.created_at}-${i}`}>
                    <div className="admin-message-meta">
                      <span className="admin-tag">{m.source === 'web' ? 'Website' : 'App'}</span>
                      {m.topic && <span className="admin-muted">{TOPICS[m.topic] || m.topic}</span>}
                      <a href={`mailto:${m.email}`}>{m.email || 'No email'}</a>
                      <span className="admin-muted">{dateTime(m.created_at)}</span>
                    </div>
                    <p>{m.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
  );
}

function minutesAgo(value) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  return minutes < 1 ? 'just now' : `${minutes} min ago`;
}

// Presence is approximate: the app syncs steps when it opens and refreshes its login about hourly.
function ActiveNow({ active }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="admin-card admin-active">
      <div className="admin-active-summary">
        <span className="admin-active-dot" aria-hidden="true" />
        <div>
          <strong>{number(active.last_15m)}</strong> active in the last 15 minutes
          <span className="admin-muted"> · {number(active.last_hour)} in the last hour</span>
        </div>
        {active.people.length > 0 && (
          <button type="button" className="admin-link" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'See who'} {open ? '▴' : '▾'}
          </button>
        )}
      </div>
      {open && (
        <ul className="admin-members admin-active-list">
          {active.people.map((p) => (
            <li key={p.id}>
              <span>{p.name || <span className="admin-muted">No name</span>}</span>
              {p.email ? <a href={`mailto:${p.email}`}>{p.email}</a> : <span className="admin-muted">–</span>}
              <span className="admin-muted">{minutesAgo(p.last_seen)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="admin-muted admin-footnote">Based on step syncs and app logins, so it means “used the app recently”, not “has it open this second”.</p>
    </div>
  );
}

const PROVIDERS = { email: 'Email', apple: 'Apple', google: 'Google' };

function matches(query, ...values) {
  const q = query.trim().toLowerCase();
  return !q || values.some((v) => v && String(v).toLowerCase().includes(q));
}

function CopyEmails({ emails, label = 'Copy emails' }) {
  const [copied, setCopied] = useState(false);
  const list = [...new Set(emails.filter(Boolean))];
  const copy = async () => {
    await navigator.clipboard.writeText(list.join(', '));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" className="admin-button admin-button-ghost" onClick={copy} disabled={list.length === 0}>
      {copied ? `Copied ${list.length}` : `${label} (${list.length})`}
    </button>
  );
}

function ConfirmButton({ user, onConfirmEmail }) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const confirm = async () => {
    setState('saving');
    setError('');
    try {
      await onConfirmEmail(user.id);
    } catch (err) {
      setError(err.message);
      setState('idle');
    }
  };
  return (
    <span className="admin-confirm">
      <button type="button" className="admin-button admin-button-small" onClick={confirm} disabled={state === 'saving'}>
        {state === 'saving' ? 'Confirming…' : 'Confirm'}
      </button>
      {error && <span className="admin-status-warn" role="alert">{error}</span>}
    </span>
  );
}

function UsersList({ users, initialFilter = 'all', onConfirmEmail }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(initialFilter);
  const shown = users.filter((u) =>
    matches(query, u.name, u.email) &&
    (filter === 'all' ||
      (filter === 'unconfirmed' && !u.email_confirmed) ||
      (filter === 'no-steps' && !u.last_step_date) ||
      (filter === 'in-challenge' && u.challenges > 0)),
  );

  return (
    <main className="admin-main">
      <section>
        <h2>Users</h2>
        <div className="admin-toolbar">
          <input
            type="search"
            className="admin-search"
            placeholder="Search name or email"
            aria-label="Search users"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className="admin-select" aria-label="Filter users" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All users</option>
            <option value="unconfirmed">Unconfirmed email</option>
            <option value="no-steps">Never synced steps</option>
            <option value="in-challenge">In a challenge</option>
          </select>
          <span className="admin-muted">{number(shown.length)} of {number(users.length)}</span>
          <CopyEmails emails={shown.map((u) => u.email)} />
        </div>
        <div className="admin-card admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Signed up</th><th>Method</th><th>Confirmed</th>
                <th>Last steps</th><th className="admin-num">Challenges</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id}>
                  <td>{u.name || <span className="admin-muted">No name</span>}</td>
                  <td>{u.email ? <a href={`mailto:${u.email}`}>{u.email}</a> : <span className="admin-muted">–</span>}</td>
                  <td>{dateTime(u.created_at)}</td>
                  <td>{PROVIDERS[u.provider] || u.provider}</td>
                  <td>
                    {u.email_confirmed ? (
                      'Yes'
                    ) : u.provider === 'email' ? (
                      <ConfirmButton user={u} onConfirmEmail={onConfirmEmail} />
                    ) : (
                      <span className="admin-status-warn">No</span>
                    )}
                  </td>
                  <td>{u.last_step_date ? shortDate(u.last_step_date) : <span className="admin-muted">Never</span>}</td>
                  <td className="admin-num">{number(u.challenges)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <p className="admin-muted admin-empty">No users match.</p>}
        </div>
      </section>
    </main>
  );
}

function ChallengesList({ challenges }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [open, setOpen] = useState(null);
  const shown = challenges.filter((c) =>
    matches(query, c.name, c.invite_code, c.creator?.name, c.creator?.email, c.location) &&
    (status === 'all' || c.status === status),
  );

  return (
    <main className="admin-main">
      <section>
        <h2>Challenges</h2>
        <div className="admin-toolbar">
          <input
            type="search"
            className="admin-search"
            placeholder="Search name, code or creator"
            aria-label="Search challenges"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className="admin-select" aria-label="Filter challenges" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All challenges</option>
            <option value="active">Active</option>
            <option value="upcoming">Upcoming</option>
            <option value="ended">Ended</option>
          </select>
          <span className="admin-muted">{number(shown.length)} of {number(challenges.length)}</span>
        </div>
        <div className="admin-card admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th><th>Status</th><th>Starts</th><th>Days</th><th>Visibility</th><th>Code</th>
                <th>Created by</th><th className="admin-num">Members</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td>
                      {c.name}
                      {c.official && <span className="admin-tag">Official</span>}
                      {c.hidden && <span className="admin-tag admin-tag-muted">Hidden</span>}
                      {c.archived && <span className="admin-tag admin-tag-muted">Archived</span>}
                    </td>
                    <td><span className={`admin-status admin-status-${c.status}`}>{c.status}</span></td>
                    <td>{shortDate(c.start_date)}</td>
                    <td>{c.days}</td>
                    <td>{c.visibility}</td>
                    <td><code>{c.invite_code}</code></td>
                    <td>
                      {c.creator ? (
                        <>
                          {c.creator.name && <span>{c.creator.name} </span>}
                          {c.creator.email && <a href={`mailto:${c.creator.email}`}>{c.creator.email}</a>}
                        </>
                      ) : (
                        <span className="admin-muted">Walk It Off</span>
                      )}
                    </td>
                    <td className="admin-num">
                      <button
                        type="button"
                        className="admin-link"
                        aria-expanded={open === c.id}
                        onClick={() => setOpen(open === c.id ? null : c.id)}
                      >
                        {number(c.members.length)} {open === c.id ? '▴' : '▾'}
                      </button>
                    </td>
                  </tr>
                  {open === c.id && (
                    <tr className="admin-members-row">
                      <td colSpan={8}>
                        {c.members.length === 0 ? (
                          <p className="admin-muted">No members yet.</p>
                        ) : (
                          <>
                            <div className="admin-members-head">
                              <strong>Members</strong>
                              <CopyEmails emails={c.members.map((m) => m.email)} label="Copy member emails" />
                            </div>
                            <ul className="admin-members">
                              {c.members.map((m, i) => (
                                <li key={`${m.email}-${i}`}>
                                  <span>{m.name || <span className="admin-muted">No name</span>}</span>
                                  {m.email ? <a href={`mailto:${m.email}`}>{m.email}</a> : <span className="admin-muted">–</span>}
                                  <span className="admin-muted">Joined {dateTime(m.joined_at)}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <p className="admin-muted admin-empty">No challenges match.</p>}
        </div>
      </section>
    </main>
  );
}

function Admin() {
  const [session, setSession] = useState(loadSession);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    document.title = 'Team dashboard | Walk It Off';
  }, []);

  const signOut = useCallback(() => {
    saveSession(null);
    setSession(null);
    setData(null);
  }, []);

  const confirmEmail = useCallback(async (userId) => {
    const current = await freshSession(session);
    if (current !== session) setSession(current);
    await request('/rest/v1/rpc/admin_confirm_email', { p_user_id: userId }, current.access);
    setData((prev) => ({
      ...prev,
      users: { ...prev.users, unconfirmed_email: Math.max(0, prev.users.unconfirmed_email - 1) },
      users_list: prev.users_list.map((u) => (u.id === userId ? { ...u, email_confirmed: true } : u)),
    }));
  }, [session]);

  const load = useCallback(async () => {
    if (!session) return;
    setRefreshing(true);
    setError('');
    try {
      const current = await freshSession(session);
      if (current !== session) setSession(current);
      const [dashboard, usersList, challengesList, activeNow] = await Promise.all([
        request('/rest/v1/rpc/admin_dashboard', {}, current.access),
        request('/rest/v1/rpc/admin_users_list', {}, current.access),
        request('/rest/v1/rpc/admin_challenges_list', {}, current.access),
        // Optional: the rest of the dashboard still loads if this one fails.
        request('/rest/v1/rpc/admin_active_now', {}, current.access).catch(() => null),
      ]);
      setData({ ...dashboard, users_list: usersList, challenges_list: challengesList, active_now: activeNow });
    } catch (err) {
      if (err.code === '42501') {
        signOut();
        setError('This account doesn’t have access to the team dashboard.');
      } else if (err.status === 401 || err.status === 400) {
        signOut();
        setError('Your session expired. Sign in again.');
      } else {
        setError(err.message);
      }
    } finally {
      setRefreshing(false);
    }
    // Only reload when the access token changes, not when the session object is refreshed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access, signOut]);

  useEffect(() => {
    load();
  }, [load]);

  if (!session) {
    return (
      <>
        {error && <p className="admin-error admin-banner" role="alert">{error}</p>}
        <SignIn onSignedIn={setSession} />
      </>
    );
  }
  if (!data) {
    return (
      <main className="admin-signin">
        {error ? (
          <div className="admin-card">
            <p className="admin-error" role="alert">{error}</p>
            <button type="button" className="admin-button" onClick={load}>Try again</button>
          </div>
        ) : (
          <p className="admin-muted" role="status">Loading dashboard…</p>
        )}
      </main>
    );
  }
  return (
    <>
      {error && <p className="admin-error admin-banner" role="alert">{error}</p>}
      <Dashboard data={data} onRefresh={load} onSignOut={signOut} onConfirmEmail={confirmEmail} refreshing={refreshing} />
    </>
  );
}

export default Admin;
